import React from 'react';
import {Slider, SliderTrack, SliderFilledTrack, SliderThumb, SliderMark} from '@chakra-ui/react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, UiQuestionnaireLayout, UiText, UiTextarea} from '~/components/elements';
import {HOT_FLASHES_TREATMENT_KEYS} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const labelStyles = {
  mt: '-2.5',
  width: '1',
  height: '5',
  backgroundColor: '#eef5f4',
  zIndex: '1',
};

const Questionnaire5Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === HOT_FLASHES_TREATMENT_KEYS.DESCRIBE_CURRENT_SYMPTOMS);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[HOT_FLASHES_TREATMENT_KEYS.DESCRIBE_CURRENT_SYMPTOMS]?.data,
  );
  const descIndex = questionnaire?.options?.findIndex(opt => opt?.value === currentAnswer?.selectedOption);
  const {inputs, setInputs} = useInput({
    selectedOption: descIndex === -1 || !descIndex ? 0 : descIndex,
    aboutSymptoms: currentAnswer?.aboutSymptoms || '',
  });
  const optionLength = questionnaire?.options?.length;
  const isLastOption = optionLength && inputs?.selectedOption === optionLength - 1;

  const isDisabled = (!isLastOption && !inputs?.aboutSymptoms) || !!isLastOption;

  const onClickSubmit = () => {
    const selectedOption = questionnaire?.options?.[inputs?.selectedOption];
    const data = isLastOption
      ? {selectedOption: selectedOption?.value}
      : {
          selectedOption: selectedOption?.value,
          aboutSymptoms: inputs?.aboutSymptoms,
        };
    dispatch(
      setQuestionnaireAnswer({
        key: HOT_FLASHES_TREATMENT_KEYS.DESCRIBE_CURRENT_SYMPTOMS,
        index: questionnaire?.index || '',
        data,
      }),
    );
    dispatch(setNextQuestionnaire({value: HOT_FLASHES_TREATMENT_KEYS.HAVING_REGULAR_PERIODS}));
  };

  const renderDesc = () => {
    const desc = questionnaire?.options?.[inputs?.selectedOption];
    return (
      <>
        <UiText fontWeight={700} fontSize={18} color="fadedJade" mb={4}>
          {desc?.label}
        </UiText>
        <UiText fontWeight={600} fontSize={14} color="#8A0000CC">
          {desc?.description}
        </UiText>
      </>
    );
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisabled}
    >
      <Col className="slider-wrapper" alignItems="center" textAlign="center">
        <UiText my={10} variant="mdqTitle">
          {questionnaire?.questions?.default}
        </UiText>
        <Slider
          min={0}
          max={optionLength ? optionLength - 1 : 3}
          step={1}
          aria-label="slider-ex-1"
          defaultValue={inputs?.selectedOption}
          onChange={val => setInputs({selectedOption: val})}
          mb={10}
        >
          <SliderMark value={1} {...labelStyles}></SliderMark>
          <SliderMark value={2} {...labelStyles}></SliderMark>
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb style={{border: '2px solid #457672', width: '17px', height: '17px'}} />
        </Slider>
        {renderDesc()}
        {!isLastOption && (
          <>
            <UiText fontSize="md" fontWeight={700} mt={spacing.mLarge}>
              {questionnaire?.questions?.sub}
            </UiText>
            {questionnaire?.elementType?.name === 'inputArea' && (
              <UiTextarea
                mt={spacing.tiny}
                bg="white"
                borderRadius={spacing.tiny}
                placeholder={questionnaire?.elementType?.placeholder}
                value={inputs.aboutSymptoms}
                onChange={({target}) => setInputs({aboutSymptoms: target.value})}
                minH="100px"
              />
            )}
          </>
        )}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire5Screen;
